/**
 * Fine Appeal — calls the `analyzeFine` Cloud Function with a photo of a
 * parking/traffic fine and returns the AI-extracted fields plus a
 * ready-to-submit Hebrew appeal letter.
 *
 * The image is downscaled client-side (via `prepareImage`) and sent as
 * base64. The server (Claude Sonnet 4.5 with vision) parses the fine and
 * drafts the letter; we surface the structured result to the UI.
 *
 * @example
 *   const prepared = await prepareImage(file);
 *   const result = await analyze(prepared);
 *   // → { analysis: { extracted, appealPotential, appealLetter } }
 */

import { auth, ref, getFirebase } from '../../core/firebase.js';
import { LocalState } from '../../core/localState.js';
import { Firebase } from '../../app/config.js';
import { createLogger } from '../../core/logger.js';
import { getStatus as getLicenseStatus } from '../vehicle/license.service.js';

const log = createLogger('fine-appeal');

/**
 * @typedef {Object} ExtractedFine
 * @property {string} fineNumber
 * @property {string} issuedDate
 * @property {string} issuedTime
 * @property {string} location
 * @property {string} section
 * @property {string} violationType
 * @property {string} amount
 * @property {string} deadline
 * @property {string} issuingAuthority
 */

/**
 * @typedef {Object} AppealPotential
 * @property {number} score
 * @property {string[]} reasons
 * @property {string} advice
 */

/**
 * @typedef {Object} FineAnalysis
 * @property {ExtractedFine} extracted
 * @property {AppealPotential} appealPotential
 * @property {string} appealLetter
 */

/**
 * Light vehicle context for the prompt — gives the AI enough info to
 * reference the owner's specific car in the appeal letter.
 */
async function buildVehicleContext() {
  const plate = String(LocalState.plate || '').replace(/\D/g, '');
  if (!plate) return null;
  try {
    const govSnap = await ref(`vehicles/${plate}/gov/vehicle`).once('value');
    const gov = govSnap.val() || {};
    /** @type {any} */ let license = {};
    try { license = await getLicenseStatus(plate); } catch (_) { /* ignore */ }
    return {
      plate,
      year: gov.shnat_yitzur || LocalState.carYear || '',
      make: gov.tozeret_nm || '',
      model: gov.kinuy_mishari || gov.degem_nm || '',
      licenseExpiry: license.prettyDate || '',
    };
  } catch (err) {
    log.warn('buildVehicleContext:', err?.message);
    return null;
  }
}

/**
 * Send a prepared fine image to the server for analysis.
 *
 * @param {import('../../shared/imageUtil.js').PreparedImage} image
 * @param {{ userLocation?: any }} [opts]
 * @returns {Promise<{ analysis: FineAnalysis | null, error: string | null, quotaLeft: number }>}
 */
export async function analyze(image, opts = {}) {
  const uid = auth().currentUser?.uid;
  if (!uid) throw new Error('not authenticated');
  if (!image || !image.data) throw new Error('no image');

  const vehicleContext = await buildVehicleContext();
  const userLocation = opts.userLocation || null;

  const fb = /** @type {any} */ (getFirebase());
  const callable = fb.app().functions(Firebase.functionsRegion).httpsCallable('analyzeFine');
  const result = await callable({
    image: { mediaType: image.mediaType, data: image.data },
    vehicleContext,
    userLocation,
  });
  const data = result?.data || {};
  if (data.ok === false) {
    return { analysis: null, error: String(data.error || 'AI could not parse this fine'), quotaLeft: Number(data.quotaLeft || 0) };
  }
  if (!data.ok || !data.analysis) throw new Error('analyzeFine returned empty');
  return { analysis: data.analysis, error: null, quotaLeft: Number(data.quotaLeft || 0) };
}
